import { z } from "zod";
import {
  ImageSet,
  ImageSource,
  ProfileId,
  ProfileSummary,
  Timestamp,
  videoFileFields,
} from "../../common";
import { AssetUsagePeriod } from "../../common/asset-usage";
import { CreatedFrom } from "../../common/created-from";
import { ItemModeration, MandatoryTags } from "../../moderation/v1alpha1/schemas";
import { PresetListing } from "../../presets/v1alpha1/schemas";

const EXAMPLE = {
  assetId: "0199f0c4-7a2b-7c3d-9e4f-5a6b7c8d9e0f",
  collectionId: "0199f0c2-1a3b-7c4d-8e5f-6a7b8c9d0e1f",
  versionId: "0199f0c4-8c1d-7e2f-a3b4-c5d6e7f80912",
  forkedCollectionId: "0199f0c2-8b9c-7d0e-9f1a-2b3c4d5e6f70",
  uploadId: "k3v9x2mq7tfa",
  usageId: "0199f0c6-3e4f-7a5b-8c6d-7e8f9a0b1c2d",
  timestamp: "2026-09-30T17:00:00.000Z",
  expiresAt: "2026-09-30T18:00:00.000Z",
  cursor: "eyJzIjoibmV3ZXN0IiwiayI6WyIyMDI2LTA5LTMwVDE3OjAwOjAwLjAwMFoiLCIwMTk5ZjBjNCJdfQ",
  media: "https://paxusercontent.co/assets",
  author: {
    profileId: "0199f0be-4d1a-7b55-8c71-59a0d2e6c884",
    username: "Grace_Hopper",
    displayName: "Grace Hopper",
    avatar: null,
  },
} as const;

export const ASSET_KINDS = ["image", "video", "audio", "basemap"] as const;

export const ASSET_TITLE_MAX_CHARACTERS = 120;
export const ASSET_LABEL_MAX_CHARACTERS = 40;
export const ASSET_MAX_COLLABORATORS = 50;
export const ASSET_DESCRIPTION_MIN_CHARACTERS = 10;
export const ASSET_DESCRIPTION_MAX_CHARACTERS = 5000;
export const ASSET_TAG_MAX_COUNT = 8;
export const ASSET_TAG_MAX_CHARACTERS = 40;
/** How many versions of its file a draft keeps. */
/** A search with a query ranks at most this many results; paging stops there. */
export const ASSET_SEARCH_MAX_RESULTS = 200;
export const ASSET_SEARCH_MAX_COLLECTIONS = 10;
/** How many of an asset's copies are listed at most. */
export const ASSET_COPIES_MAX = 10;
/** How many assets each list of an asset's similar assets holds at most. */
export const ASSET_SIMILAR_MAX_PER_LIST = 10;
/** The most scenarios listed as using one asset. */
export const ASSET_PRESETS_MAX = 12;

/** The longest side of a picture's full size: an image, or a video's poster. */
export const ASSET_PICTURE_MAX_SIDE = 2048;
/** The longest side of an animated image's full size. Animation costs far more to encode and to load. */
export const ASSET_ANIMATION_MAX_SIDE = 1024;
/** Each picture's sizes as fractions of its full size: full, half, a quarter, and an eighth. */
export const ASSET_PICTURE_SCALES = [1, 1 / 2, 1 / 4, 1 / 8] as const;
/** A size smaller than this on its longest side is left out, so a small upload has fewer sizes. */
export const ASSET_PICTURE_MIN_SIDE = 64;
/** The longest side of the JPEG still that notifications and email show. */
export const ASSET_STILL_MAX_SIDE = 1024;
/** The side of an image's square icon. */
export const ASSET_ICON_SIDE = 256;
/** A video's full size fits inside this box, turned to match the video, so 1080p in either orientation. */
export const ASSET_VIDEO_MAX_SIDES = { long: 1920, short: 1080 } as const;

/** The longest slug: a title cut to about 60 characters, with room for a number after it. */
export const ASSET_SLUG_MAX_CHARACTERS = 80;
export const ASSET_COLLECTION_TITLE_MAX_CHARACTERS = 80;
export const ASSET_COLLECTION_DESCRIPTION_MAX_CHARACTERS = 1000;
export const ASSET_COLLECTION_MAX_ASSETS = 10_000;
export const ASSET_COLLECTION_MAX_COLLABORATORS = 50;
/** A collection search ranks at most this many results; paging stops there. */
export const ASSET_COLLECTION_SEARCH_MAX_RESULTS = 200;

/** `zxx` is ISO 639's code for no linguistic content, such as a flag with no words on it. */
export const ASSET_LANGUAGES = [
  "zxx",
  "ar",
  "bn",
  "cs",
  "de",
  "en",
  "es",
  "fa",
  "fr",
  "hi",
  "hu",
  "id",
  "is",
  "it",
  "ja",
  "ko",
  "ms",
  "nl",
  "pl",
  "pt",
  "ru",
  "sv",
  "sw",
  "th",
  "tl",
  "tr",
  "uk",
  "ur",
  "vi",
  "zh",
] as const;

export const ASSET_UPLOAD_CONTENT_TYPES = {
  image: ["image/jpeg", "image/png", "image/webp", "image/gif"],
  video: ["video/mp4", "video/quicktime", "video/webm"],
  audio: [
    "audio/mpeg",
    "audio/mp4",
    "audio/x-m4a",
    "audio/aac",
    "audio/ogg",
    "audio/wav",
    "audio/x-wav",
    "audio/wave",
  ],
} as const;

const MIB = 1024 * 1024;
export const ASSET_UPLOAD_MAX_BYTES = {
  image: 25 * MIB,
  video: 256 * MIB,
  audio: 128 * MIB,
} as const;

const UPLOADABLE_CONTENT_TYPES = [
  ...ASSET_UPLOAD_CONTENT_TYPES.image,
  ...ASSET_UPLOAD_CONTENT_TYPES.video,
  ...ASSET_UPLOAD_CONTENT_TYPES.audio,
] as const;

/** The most bytes any kind that takes this type of file accepts. The draft's own kind sets its limit. */
function largestUpload(contentType: AssetUploadContentType): number {
  const kinds = Object.keys(
    ASSET_UPLOAD_CONTENT_TYPES,
  ) as (keyof typeof ASSET_UPLOAD_CONTENT_TYPES)[];
  return Math.max(
    ...kinds
      .filter(kind => (ASSET_UPLOAD_CONTENT_TYPES[kind] as readonly string[]).includes(contentType))
      .map(kind => ASSET_UPLOAD_MAX_BYTES[kind]),
  );
}

const TAG_PATTERN = `[a-z0-9](?:[a-z0-9-]{0,${ASSET_TAG_MAX_CHARACTERS - 2}}[a-z0-9])?`;
const UUID_PATTERN = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const commaSeparated = (item: string, max: number) =>
  new RegExp(`^${item}(?:,${item}){0,${max - 1}}$`);

// Ids

export const AssetId = z.uuid().brand<"AssetId">().meta({
  id: "AssetId",
  description: "The stable id of an asset.",
  example: EXAMPLE.assetId,
});
export type AssetId = z.infer<typeof AssetId>;

export const AssetCollectionId = z.uuid().brand<"AssetCollectionId">().meta({
  id: "AssetCollectionId",
  description: "The stable id of an asset collection.",
  example: EXAMPLE.collectionId,
});
export type AssetCollectionId = z.infer<typeof AssetCollectionId>;

export const AssetVersionId = z.uuid().brand<"AssetVersionId">().meta({
  id: "AssetVersionId",
  description:
    "The stable id of one draft or published version of an asset, the same before and after it's published. A world names a picture by it.",
  example: EXAMPLE.versionId,
});
export type AssetVersionId = z.infer<typeof AssetVersionId>;

export const AssetUploadId = z
  .string()
  .regex(/^[0-9a-z]{12}$/)
  .brand<"AssetUploadId">()
  .meta({
    id: "AssetUploadId",
    description:
      "The id of one file upload, 12 lowercase letters and digits. Send it to set a draft's file once the upload finishes.",
    example: EXAMPLE.uploadId,
  });
export type AssetUploadId = z.infer<typeof AssetUploadId>;

// Fields

export const AssetKind = z.enum(ASSET_KINDS).meta({
  id: "AssetKind",
  description:
    "What the asset is: an `image`, a `video`, an `audio` recording, or a `basemap` drawn under a map.",
  example: "image",
});
export type AssetKind = z.infer<typeof AssetKind>;

export const AssetVersionStatus = z
  .enum(["draft", "publishing", "refused", "in_appeal", "published", "removed"])
  .meta({
    id: "AssetVersionStatus",
    description:
      "Where the version is in its lifecycle. `draft` while its owner and collaborators edit it. `publishing`, frozen, while it's checked and its files written. `refused` when automod refused its file or its publish: editing returns it to `draft`, or its uploader can appeal. `in_appeal`, frozen, while the appeal is open. `published` once anyone can find it; a published version never changes. `removed` when a moderator took it down after a report.",
    example: "published",
  });
export type AssetVersionStatus = z.infer<typeof AssetVersionStatus>;

export const AssetListStatus = z.enum(["draft", "published"]).meta({
  id: "AssetListStatus",
  description:
    "`draft` for the assets with a draft, `published` for the ones with a published version.",
  example: "draft",
});
export type AssetListStatus = z.infer<typeof AssetListStatus>;

export const AssetTag = z
  .string()
  .regex(new RegExp(`^${TAG_PATTERN}$`))
  .meta({
    id: "AssetTag",
    description: `A tag of lowercase letters, digits, and hyphens, 1 to ${ASSET_TAG_MAX_CHARACTERS} characters, starting and ending with a letter or digit. Write spaces as hyphens.`,
    example: "flag",
  });
export type AssetTag = z.infer<typeof AssetTag>;

const AssetTags = z
  .array(AssetTag)
  .max(ASSET_TAG_MAX_COUNT)
  .refine(tags => new Set(tags).size === tags.length, "Tags must not repeat.")
  .meta({
    description: `Up to ${ASSET_TAG_MAX_COUNT} different tags. They are stored sorted.`,
    example: ["flag", "prussia"],
  });

export const AssetLanguage = z.enum(ASSET_LANGUAGES).meta({
  id: "AssetLanguage",
  description:
    "The ISO 639-1 code of the language in the asset, or `zxx` when it has no words in it. An asset has one language.",
  example: "de",
});
export type AssetLanguage = z.infer<typeof AssetLanguage>;

const AssetTitle = z
  .string()
  .trim()
  .min(1)
  .max(ASSET_TITLE_MAX_CHARACTERS)
  .meta({
    description: `The asset's name, 1 to ${ASSET_TITLE_MAX_CHARACTERS} characters.`,
    example: "Flag of the Kingdom of Prussia, 1871",
  });

const AssetDescription = z
  .string()
  .trim()
  .max(ASSET_DESCRIPTION_MAX_CHARACTERS)
  .meta({
    description: `What the asset shows or contains, in Markdown, up to ${ASSET_DESCRIPTION_MAX_CHARACTERS} characters. Publishing needs at least ${ASSET_DESCRIPTION_MIN_CHARACTERS}.`,
    example: "A black eagle on a white field, the state flag of Prussia after 1871.",
  });

export const AssetFraming = z
  .strictObject({
    x: z.number().min(0).max(1).meta({
      description:
        "The square's centre across the image, from 0 at the left edge to 1 at the right.",
      example: 0.5,
    }),
    y: z.number().min(0).max(1).meta({
      description: "The square's centre down the image, from 0 at the top edge to 1 at the bottom.",
      example: 0.4,
    }),
    zoom: z.number().min(1).max(5).meta({
      description:
        "How far the square zooms in, from 1 to 5. At 1 it is the largest square that fits in the image; at 2 it is half that width.",
      example: 1,
    }),
  })
  .meta({
    id: "AssetFraming",
    description:
      "The square an image's icon keeps, such as the emblem a game draws on the map. Near an edge, the square moves inside the image rather than centring on the point.",
    example: { x: 0.5, y: 0.4, zoom: 1 },
  });
export type AssetFraming = z.infer<typeof AssetFraming>;

export const ImageProcessingOptions = z
  .strictObject({
    kind: z.literal("image"),
    framing: AssetFraming.optional().meta({
      description:
        "The square the icon keeps. Without it, the icon keeps the region most likely to draw the eye.",
    }),
  })
  .meta({ id: "ImageProcessingOptions", description: "How an image is processed." });

export const VideoProcessingOptions = z
  .strictObject({ kind: z.literal("video") })
  .meta({ id: "VideoProcessingOptions", description: "A video has nothing to choose yet." });

export const AudioProcessingOptions = z
  .strictObject({ kind: z.literal("audio") })
  .meta({ id: "AudioProcessingOptions", description: "A recording has nothing to choose yet." });

export const AssetProcessingOptions = z
  .discriminatedUnion("kind", [
    ImageProcessingOptions,
    VideoProcessingOptions,
    AudioProcessingOptions,
  ])
  .meta({
    id: "AssetProcessingOptions",
    description:
      "What the creator chose about how a version's file is processed, kept so the version can be processed again. A choice left out is made by processing.",
    example: { kind: "image", framing: { x: 0.5, y: 0.4, zoom: 1 } },
  });
export type AssetProcessingOptions = z.infer<typeof AssetProcessingOptions>;

// Media

const Thumbhash = z.string().min(1).meta({
  description:
    "A base64 [ThumbHash](https://evanw.github.io/thumbhash/) of the asset's picture, to draw a blurred placeholder while it loads.",
  example: "3OcRJYB4d3h/iIeHeEh3eIhw+j3A",
});

const Pixels = z.int().positive();

export const ImageAssetMedia = z
  .strictObject({
    kind: z.literal("image"),
    image: ImageSet.meta({
      description: `The whole image as AVIF at full size, half, a quarter, and an eighth of it, smallest first, each in the image's own shape. Full size is the upload's, cut to ${ASSET_PICTURE_MAX_SIDE} pixels on its longest side, or ${ASSET_ANIMATION_MAX_SIDE} when animated. An animated upload stays animated in every size. A size under ${ASSET_PICTURE_MIN_SIDE} pixels on its longest side is left out.`,
    }),
    still: ImageSource.meta({
      description: `A JPEG of the image, or of its first frame with detail when animated, at most ${ASSET_STILL_MAX_SIDE} pixels on its longest side. Show it where AVIF can't go, such as notifications and email, and to viewers who prefer reduced motion.`,
    }),
    icon: ImageSource.meta({
      description: `The square the processing options' \`framing\` keeps, ${ASSET_ICON_SIDE} pixels a side, as AVIF. It is always still, even for an animated image.`,
    }),
    processingOptions: ImageProcessingOptions,
    width: Pixels.meta({ description: "The image's width in pixels.", example: 2400 }),
    height: Pixels.meta({ description: "The image's height in pixels.", example: 1600 }),
    bytes: z.int().positive().meta({
      description: "The size in bytes of the full-size file in `image`, the largest a viewer gets.",
      example: 842_117,
    }),
    animated: z.boolean().meta({ description: "Whether the image moves.", example: false }),
    durationSeconds: z.number().positive().nullable().meta({
      description:
        "How long one loop of an animated image lasts, at most 15 seconds. Null for a still image.",
      example: null,
    }),
    thumbhash: Thumbhash,
  })
  .meta({ id: "ImageAssetMedia", description: "An image asset's files." });
export type ImageAssetMedia = z.infer<typeof ImageAssetMedia>;

export const VideoSource = z
  .strictObject({
    url: z.url().meta({
      description: "Where the MP4 file is served.",
      example: `${EXAMPLE.media}/videos/cavalry-charge/full.mp4`,
    }),
    ...videoFileFields,
    frameRate: videoFileFields.frameRate.meta({
      description: "Frames per second, at most 30.",
      example: 30,
    }),
  })
  .meta({
    id: "VideoSource",
    description:
      "One size of a video, as an AV1 MP4. The web player uses the browser's decoder when available and a software decoder otherwise.",
  });
export type VideoSource = z.infer<typeof VideoSource>;

export const VideoAssetMedia = z
  .strictObject({
    kind: z.literal("video"),
    processingOptions: VideoProcessingOptions,
    sources: z
      .array(VideoSource)
      .min(1)
      .meta({
        description: `The video at half and at full size, smallest first. Full size is the upload's own, cut to fit ${ASSET_VIDEO_MAX_SIDES.long} × ${ASSET_VIDEO_MAX_SIDES.short} the way the video faces, and half is half of it on each side. Play the smallest that covers the space it is drawn in: usually half for a grid's preview, on a phone, or with a software AV1 decoder, and full on a larger screen. A half under ${ASSET_PICTURE_MIN_SIDE} pixels on its longest side is left out.`,
      }),
    poster: ImageSet.meta({
      description: `The video's most representative frame as AVIF, shown until it plays: full size, half, a quarter, and an eighth of it, smallest first. Full size is the video's, cut to ${ASSET_PICTURE_MAX_SIDE} pixels on its longest side.`,
    }),
    still: ImageSource.meta({
      description: `The poster as a JPEG, at most ${ASSET_STILL_MAX_SIDE} pixels on its longest side, for notifications and email.`,
    }),
    width: Pixels.meta({ description: "The largest source's width in pixels.", example: 1920 }),
    height: Pixels.meta({ description: "The largest source's height in pixels.", example: 1080 }),
    durationSeconds: z.number().positive().meta({
      description: "How long the video runs, at most 30 seconds.",
      example: 12.4,
    }),
    hasAudio: z.boolean().meta({ description: "Whether the video has sound.", example: true }),
    thumbhash: Thumbhash,
  })
  .meta({ id: "VideoAssetMedia", description: "A video asset's files." });
export type VideoAssetMedia = z.infer<typeof VideoAssetMedia>;

export const AudioAssetMedia = z
  .strictObject({
    kind: z.literal("audio"),
    processingOptions: AudioProcessingOptions,
    url: z.url().meta({
      description: "Where the audio is served, as Opus in an Ogg file (`audio/ogg`).",
      example: `${EXAMPLE.media}/audio/battle-drums/audio.opus`,
    }),
    bytes: z
      .int()
      .positive()
      .meta({ description: "The file's size in bytes.", example: 2_104_331 }),
    durationSeconds: z.number().positive().meta({
      description: "How long the recording runs, at most 7 minutes.",
      example: 87.2,
    }),
    waveformUrl: z.url().meta({
      description:
        "Where the waveform is served: a JSON array of 1,024 peak levels from 0 to 255, evenly spaced across the recording. To draw N bars, take the loudest peak in each Nth of the array.",
      example: `${EXAMPLE.media}/audio/battle-drums/waveform.json`,
    }),
  })
  .meta({ id: "AudioAssetMedia", description: "An audio asset's file." });
export type AudioAssetMedia = z.infer<typeof AudioAssetMedia>;

export const AssetMedia = z
  .discriminatedUnion("kind", [ImageAssetMedia, VideoAssetMedia, AudioAssetMedia])
  .meta({
    id: "AssetMedia",
    description:
      "The version's file, shaped by its kind. A published version's URLs never change. A draft's URLs are signed for its asset's owner and collaborators, so don't share them, and stop working at `mediaUrlsExpireAt`.",
  });
export type AssetMedia = z.infer<typeof AssetMedia>;

// Assets

export const AssetReaction = z.enum(["like", "dislike"]).meta({
  id: "AssetReaction",
  description: "Whether a person likes or dislikes an asset.",
  example: "like",
});
export type AssetReaction = z.infer<typeof AssetReaction>;

const AssetSlug = z
  .string()
  .min(1)
  .max(ASSET_SLUG_MAX_CHARACTERS)
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/)
  .meta({
    description:
      "The asset's name in URLs, made from its first published version's title: its words in lowercase ASCII, other scripts spelled in Latin letters (\"Россия\" becomes `rossiya`), joined by hyphens and followed by `-2`, `-3`, and so on when another asset of the kind already has the name. Unique within the kind, kept by every later version, and never given to another asset.",
    example: "flag-of-the-kingdom-of-prussia-1871",
  });

export const AssetStats = z
  .strictObject({
    likeCount: z.int().nonnegative().meta({ description: "People who like it.", example: 42 }),
    dislikeCount: z.int().nonnegative().meta({ description: "People who dislike it.", example: 3 }),
    useCount: z.int().nonnegative().meta({
      description: "How many times games have used it, counting every use a game logged.",
      example: 1280,
    }),
  })
  .meta({ id: "AssetStats", description: "How a published asset has been received and used." });

const AssetLabel = z
  .string()
  .trim()
  .min(1)
  .max(ASSET_LABEL_MAX_CHARACTERS)
  .meta({
    description: `An optional name the author gives a version in the publish panel, 1 to ${ASSET_LABEL_MAX_CHARACTERS} characters, unique among the asset's versions.`,
    example: "Winter",
  });

export const AssetVersionProblem = z
  .strictObject({
    where: z.string().meta({
      description: "What the problem is about, such as `title` or one of a basemap's rasters.",
      example: "title",
    }),
    message: z.string().meta({ description: "What's wrong.", example: "A title is needed." }),
  })
  .meta({ id: "AssetVersionProblem", description: "Why a draft's last publish failed." });
export type AssetVersionProblem = z.infer<typeof AssetVersionProblem>;
export type AssetStats = z.infer<typeof AssetStats>;

export const AssetPublishRequirement = z
  .enum(["media", "title", "description", "language", "isSensitive"])
  .meta({
    id: "AssetPublishRequirement",
    description: `Something a draft needs before it can be published. \`media\`: a file. \`title\`: a title. \`description\`: a description of at least ${ASSET_DESCRIPTION_MIN_CHARACTERS} characters. \`language\`: a language. \`isSensitive\`: an answer on sensitivity.`,
    example: "description",
  });
export type AssetPublishRequirement = z.infer<typeof AssetPublishRequirement>;

export const Asset = z
  .strictObject({
    id: AssetId,
    kind: AssetKind,
    slug: AssetSlug.nullable().meta({
      description: "The asset's slug, or null before its first version is published.",
    }),
    owner: ProfileSummary.meta({ description: "Who owns the asset." }),
    liveVersionId: AssetVersionId.nullable().meta({
      description:
        "The asset's latest published version, which discovery shows, or null before its first publish.",
    }),
    publishedVersionCount: z.int().nonnegative().meta({
      description:
        "How many published versions the asset has. Show a version number only when it has more than one.",
      example: 1,
    }),
    viewerCanEdit: z.boolean().meta({
      description:
        "Whether the caller is the asset's owner or a collaborator, who can start and edit its drafts and publish them.",
      example: false,
    }),
    archivedAt: Timestamp.nullable().meta({
      description:
        "When its owner took the asset out of browsing and searching, or null. Its versions stay readable, and collections that hold it still list it.",
      example: null,
    }),
    removedAt: Timestamp.nullable().meta({
      description:
        "When a moderator took the whole asset down, or null. Only its owner still sees a removed asset, with no public files.",
      example: null,
    }),
    versionId: AssetVersionId.meta({
      description:
        "The version every field below describes: the live one, a version asked for by id, or a draft.",
    }),
    status: AssetVersionStatus,
    version: z.int().positive().nullable().meta({
      description: "1, 2, 3 in the order the asset's versions were published, or null on a draft.",
      example: 1,
    }),
    label: AssetLabel.nullable().meta({ description: "The version's label, or null." }),
    createdFrom: CreatedFrom.optional().meta({
      description:
        "The version parent chain and its original or external source, included on detail reads.",
    }),
    createdFromVersionId: AssetVersionId.nullable().meta({
      description:
        "The published version this one was created from, of this asset or, for a copy, another. Null for one made from scratch.",
      example: null,
    }),
    title: AssetTitle.nullable().meta({
      description: "The version's name, or null on a draft whose author hasn't given one.",
    }),
    description: AssetDescription,
    usagePeriod: AssetUsagePeriod.nullable().optional(),
    tags: AssetTags,
    mandatoryTags: MandatoryTags.meta({
      description:
        "The mandatory tags the version carries, kept apart from its tags. Empty on a draft. Publishing sets every one automod finds in the file, and a moderator's ruling on the version replaces them.",
      example: [],
    }),
    language: AssetLanguage.nullable().meta({
      description: "The language in the asset, or null on a draft whose author hasn't chosen one.",
    }),
    isSensitive: z.boolean().nullable().meta({
      description:
        "Whether the author marked it sensitive, such as graphic violence. Blur a sensitive asset until the viewer chooses to see it. Null on a draft whose author hasn't chosen.",
      example: false,
    }),
    isAiGenerated: z.boolean().nullable().meta({
      description: "Whether the asset was made with generative AI, or null while that is unknown.",
      example: null,
    }),
    isHumanVerified: z.boolean().meta({
      description: "Whether Pax staff checked that the asset is what it says it is.",
      example: false,
    }),
    wasUniqueAtPublish: z.boolean().nullable().meta({
      description:
        "Whether no copy of the version had been published when it was: nothing of its kind made from the very same file, or at least 95% alike by its picture, frames, recording, or the meaning of its title, description, and tags. The asset's own earlier versions never count. Null on a draft, and when nothing matched but its file had nothing to compare, such as silent audio.",
      example: true,
    }),
    media: AssetMedia.nullable().meta({
      description:
        "The version's file, or null on a draft that has no file yet, a removed version, and a basemap, whose files are its manifest's.",
    }),
    missingForPublish: z.array(AssetPublishRequirement).meta({
      description:
        "What the draft still needs before it can be published, in the order a form shows them. Empty when it is ready, and always empty once published.",
      example: [],
    }),
    problems: z.array(AssetVersionProblem).nullable().meta({
      description: "Why the draft's last publish failed, or null.",
      example: null,
    }),
    stats: AssetStats.nullable().meta({
      description: "Null until the asset's first version is published.",
    }),
    viewerReaction: AssetReaction.nullable().meta({
      description: "The caller's reaction, or null when they have none or aren't signed in.",
      example: null,
    }),
    mediaUrlsExpireAt: Timestamp.nullable().meta({
      description:
        "When a draft's signed media URLs stop working; fetch the asset again for new ones. Null on a draft with no file yet, and once published, because published URLs never expire.",
      example: null,
    }),
    publishedAt: Timestamp.nullable().meta({
      description: "When the version was published, or null on a draft.",
    }),
    createdAt: Timestamp.meta({ description: "When the version was started." }),
    updatedAt: Timestamp.meta({ description: "When the version last changed." }),
  })
  .meta({
    id: "Asset",
    description:
      "An asset at one of its versions: what every version shares, then that version's file and what its author says about it.",
    example: {
      id: EXAMPLE.assetId,
      kind: "image",
      slug: "flag-of-the-kingdom-of-prussia-1871",
      owner: EXAMPLE.author,
      liveVersionId: EXAMPLE.versionId,
      publishedVersionCount: 1,
      viewerCanEdit: false,
      archivedAt: null,
      removedAt: null,
      versionId: EXAMPLE.versionId,
      status: "published",
      version: 1,
      label: null,
      createdFromVersionId: null,
      title: "Flag of the Kingdom of Prussia, 1871",
      description: "A black eagle on a white field, the state flag of Prussia after 1871.",
      tags: ["flag", "prussia"],
      mandatoryTags: [],
      language: "zxx",
      isSensitive: false,
      isAiGenerated: null,
      isHumanVerified: false,
      wasUniqueAtPublish: true,
      media: {
        kind: "image",
        image: {
          sources: (
            [
              ["eighth", 256],
              ["quarter", 512],
              ["half", 1024],
              ["full", 2048],
            ] as const
          ).map(([size, width]) => ({
            url: `${EXAMPLE.media}/images/flag-of-the-kingdom-of-prussia-1871/${size}.avif`,
            width,
            height: Math.round((width * 2) / 3),
          })),
        },
        still: {
          url: `${EXAMPLE.media}/images/flag-of-the-kingdom-of-prussia-1871/still.jpg`,
          width: 1024,
          height: 683,
        },
        icon: {
          url: `${EXAMPLE.media}/images/flag-of-the-kingdom-of-prussia-1871/icon.avif`,
          width: 256,
          height: 256,
        },
        processingOptions: { kind: "image", framing: { x: 0.5, y: 0.5, zoom: 1 } },
        width: 2400,
        height: 1600,
        animated: false,
        durationSeconds: null,
        thumbhash: "3OcRJYB4d3h/iIeHeEh3eIhw+j3A",
      },
      stats: { likeCount: 42, dislikeCount: 3, useCount: 1280 },
      viewerReaction: null,
      missingForPublish: [],
      problems: null,
      mediaUrlsExpireAt: null,
      publishedAt: EXAMPLE.timestamp,
      createdAt: EXAMPLE.timestamp,
      updatedAt: EXAMPLE.timestamp,
    },
  });
export type Asset = z.infer<typeof Asset>;

export const AssetResponse = z
  .strictObject({
    asset: Asset,
    moderation: ItemModeration.optional().meta({
      description:
        "The version's moderation state: a refused draft, its latest appeal, or a removal. Returned only to the asset's owner and collaborators, by Get an asset.",
    }),
  })
  .meta({ id: "AssetResponse", description: "One asset." });
export type AssetResponse = z.infer<typeof AssetResponse>;

// Uploads

export type AssetUploadContentType = (typeof UPLOADABLE_CONTENT_TYPES)[number];

export const CreateAssetUploadRequest = z
  .strictObject({
    contentType: z.enum(UPLOADABLE_CONTENT_TYPES).meta({
      description:
        "The file's type, which must be one the draft's kind accepts. The upload URL accepts only this type.",
      example: "image/png",
    }),
    bytes: z.int().min(1).meta({
      description:
        "The file's size in bytes: at most 25 MiB for an image, 256 MiB for a video, or 128 MiB for audio. The upload URL accepts only a file of exactly this size.",
      example: 3_482_113,
    }),
  })
  .refine(
    file => file.bytes <= largestUpload(file.contentType),
    "The file is over its kind's size limit.",
  )
  .meta({
    id: "CreateAssetUploadRequest",
    description: "The file the client is about to upload to the draft.",
    example: { contentType: "image/png", bytes: 3_482_113 },
  });
export type CreateAssetUploadRequest = z.infer<typeof CreateAssetUploadRequest>;

export const AssetUpload = z
  .strictObject({
    url: z.url().meta({
      description: "A presigned storage URL. Send the file's bytes to it as the request body.",
      example:
        "https://uploads.example.r2.cloudflarestorage.com/assets/k3v9x2mq7tfa?X-Amz-Signature=…",
    }),
    method: z.literal("PUT"),
    headers: z
      .strictObject({
        "Content-Type": z.enum(UPLOADABLE_CONTENT_TYPES).meta({ example: "image/png" }),
      })
      .meta({ description: "Send exactly these headers with the file." }),
    expiresAt: Timestamp.meta({
      description: "When the URL stops accepting the upload, 5 minutes after it was made.",
      example: EXAMPLE.expiresAt,
    }),
  })
  .meta({ id: "AssetUpload", description: "Where and how to upload the file." });
export type AssetUpload = z.infer<typeof AssetUpload>;

export const CreateAssetUploadResponse = z
  .strictObject({ uploadId: AssetUploadId, upload: AssetUpload })
  .meta({
    id: "CreateAssetUploadResponse",
    description: "The upload's id, and where to upload the file.",
  });
export type CreateAssetUploadResponse = z.infer<typeof CreateAssetUploadResponse>;

export const AssetMediaRejection = z
  .strictObject({
    reason: z
      .enum([
        "unsupported_format",
        "too_large",
        "too_small",
        "too_long",
        "decode_failed",
        "explicit_content",
      ])
      .meta({
        description:
          "Why the file can't be used. `explicit_content`: Google's SafeSearch rates the image, or a video's still, as likely sexually explicit; it is refused before automod, isn't stored, and can't be appealed. `unsupported_format`: its bytes aren't a format its kind accepts. `too_large`: it is over its size or pixel limit, or an animation has too many frames. `too_small`: a video is under 10 pixels on a side. `too_long`: a video runs over 30 seconds, audio over 7 minutes, or an animation over 15 seconds. `decode_failed`: the file is damaged.",
        example: "unsupported_format",
      }),
  })
  .meta({
    id: "AssetMediaRejection",
    description: "Why the uploaded file can't be used.",
    example: { reason: "unsupported_format" },
  });
export type AssetMediaRejection = z.infer<typeof AssetMediaRejection>;

// Authoring

export const CreateAssetRequest = z
  .strictObject({
    kind: AssetKind.meta({
      description:
        "What the asset will be. It never changes, and decides which files its drafts accept.",
      example: "image",
    }),
    copyOf: AssetVersionId.optional().meta({
      description:
        "A published version of an asset of the same kind to copy, for Make a copy. The first draft begins from its details and files, and names it as `createdFromVersionId`.",
    }),
  })
  .meta({
    id: "CreateAssetRequest",
    description: "The kind of asset to start, and optionally the version it copies.",
    example: { kind: "image" },
  });

export const StartAssetDraftRequest = z
  .strictObject({
    createdFromVersionId: AssetVersionId.optional().meta({
      description:
        "The published version the draft begins from, copying its details and files. Without it, the draft begins from the live version, or from nothing before the first publish.",
    }),
  })
  .meta({
    id: "StartAssetDraftRequest",
    description: "Which version a new draft begins from.",
    example: { createdFromVersionId: EXAMPLE.versionId },
  });
export type StartAssetDraftRequest = z.infer<typeof StartAssetDraftRequest>;

export const SetAssetArchivedRequest = z
  .strictObject({
    archived: z.boolean().meta({
      description: "`true` takes the asset out of browsing and searching, `false` lists it again.",
      example: true,
    }),
  })
  .meta({ id: "SetAssetArchivedRequest", description: "Whether the asset is archived." });
export type SetAssetArchivedRequest = z.infer<typeof SetAssetArchivedRequest>;

export const SetAssetSignalsRequest = z
  .strictObject({
    isAiGenerated: z.boolean().nullable().optional().meta({
      description:
        "Whether generative AI made the version, or null while that is unknown. Leave it out to keep it.",
      example: false,
    }),
    isHumanVerified: z.boolean().optional().meta({
      description:
        "Whether Pax staff checked that the version is what its title, description, and tags say. Leave it out to keep it.",
      example: true,
    }),
  })
  .refine(
    body => body.isAiGenerated !== undefined || body.isHumanVerified !== undefined,
    "Send at least one field.",
  )
  .meta({
    id: "SetAssetSignalsRequest",
    description: "The signals Pax sets on a published version. Leave a field out to keep it.",
    example: { isAiGenerated: false, isHumanVerified: true },
  });
export type SetAssetSignalsRequest = z.infer<typeof SetAssetSignalsRequest>;
export type CreateAssetRequest = z.infer<typeof CreateAssetRequest>;

export const UpdateAssetRequest = z
  .strictObject({
    title: AssetTitle.nullable().optional(),
    description: AssetDescription.optional(),
    tags: AssetTags.optional(),
    language: AssetLanguage.nullable().optional(),
    isSensitive: z.boolean().nullable().optional(),
    usagePeriod: AssetUsagePeriod.nullable().optional(),
  })
  .refine(
    body => Object.values(body).some(value => value !== undefined),
    "Send at least one field.",
  )
  .meta({
    id: "UpdateAssetRequest",
    description:
      "The draft's fields to change. Leave a field out to keep it; send null to clear it.",
    example: {
      description: "A black eagle on a white field, the state flag of Prussia after 1871.",
    },
  });
export type UpdateAssetRequest = z.infer<typeof UpdateAssetRequest>;

export const AddAssetMediaRequest = z
  .strictObject({
    uploadId: AssetUploadId,
    processingOptions: AssetProcessingOptions.optional().meta({
      description:
        "How to process the upload, for the draft's kind. Without them, processing makes every choice.",
    }),
  })
  .meta({
    id: "AddAssetMediaRequest",
    description:
      "The upload to make the draft's file, replacing any it had. It must be a file the draft's kind accepts.",
    example: { uploadId: EXAMPLE.uploadId },
  });
export type AddAssetMediaRequest = z.infer<typeof AddAssetMediaRequest>;

export const UpdateAssetMediaRequest = z
  .strictObject({
    processingOptions: AssetProcessingOptions.meta({
      description: "New processing options for the draft's file. An image's icon is made again.",
    }),
  })
  .meta({
    id: "UpdateAssetMediaRequest",
    description: "How a draft's upload is processed again.",
    example: { processingOptions: { kind: "image", framing: { x: 0.5, y: 0.4, zoom: 1.5 } } },
  });
export type UpdateAssetMediaRequest = z.infer<typeof UpdateAssetMediaRequest>;

export const AssetVersionsResponse = z
  .strictObject({
    versions: z.array(Asset).meta({
      description:
        "The asset's published versions, newest first, removed ones included, then its drafts, newest first, for its owner and collaborators.",
    }),
  })
  .meta({ id: "AssetVersionsResponse", description: "An asset's versions." });
export type AssetVersionsResponse = z.infer<typeof AssetVersionsResponse>;

export const PublishAssetRequest = z
  .strictObject({
    attestsCopyright: z.literal(true).meta({
      description:
        "The author's confirmation that the version doesn't violate any copyrights or trademarks, from the box they tick to publish. Publishing records when they gave it.",
      example: true,
    }),
    label: AssetLabel.optional(),
  })
  .meta({
    id: "PublishAssetRequest",
    description: "What the author confirms when they publish, and the version's optional label.",
    example: { attestsCopyright: true },
  });
export type PublishAssetRequest = z.infer<typeof PublishAssetRequest>;

export const AssetPublishProblems = z
  .strictObject({
    missing: z
      .array(AssetPublishRequirement)
      .min(1)
      .meta({
        description: "What the draft still needs, the same list as its `missingForPublish`.",
        example: ["description", "isSensitive"],
      }),
  })
  .meta({
    id: "AssetPublishProblems",
    description: "Why the draft can't be published yet.",
    example: { missing: ["description", "isSensitive"] },
  });
export type AssetPublishProblems = z.infer<typeof AssetPublishProblems>;

export const SetAssetReactionRequest = z.strictObject({ value: AssetReaction }).meta({
  id: "SetAssetReactionRequest",
  description: "The caller's reaction. It replaces any reaction they had.",
  example: { value: "like" },
});
export type SetAssetReactionRequest = z.infer<typeof SetAssetReactionRequest>;

export const AssetDraftParams = z.strictObject({ id: AssetId, versionId: AssetVersionId }).meta({
  id: "AssetDraftParams",
  description: "Route parameters naming one asset and one of its drafts.",
});
export type AssetDraftParams = z.infer<typeof AssetDraftParams>;

export const AssetVersionParams = z.strictObject({ id: AssetId, versionId: AssetVersionId }).meta({
  id: "AssetVersionParams",
  description: "Route parameters naming one asset and one of its versions.",
});
export type AssetVersionParams = z.infer<typeof AssetVersionParams>;

export const AssetCollaboratorParams = z.strictObject({ id: AssetId, userId: ProfileId }).meta({
  id: "AssetCollaboratorParams",
  description: "Route parameters naming one asset and one person.",
});
export type AssetCollaboratorParams = z.infer<typeof AssetCollaboratorParams>;

export const AssetCollaborator = z
  .strictObject({
    profile: ProfileSummary,
    addedAt: Timestamp.meta({ description: "When the owner added them." }),
  })
  .meta({ id: "AssetCollaborator", description: "A person who edits an asset's drafts." });
export type AssetCollaborator = z.infer<typeof AssetCollaborator>;

export const AssetCollaboratorsResponse = z
  .strictObject({
    collaborators: z
      .array(AssetCollaborator)
      .max(ASSET_MAX_COLLABORATORS)
      .meta({ description: `Up to ${ASSET_MAX_COLLABORATORS}, oldest first.` }),
  })
  .meta({ id: "AssetCollaboratorsResponse", description: "An asset's collaborators." });
export type AssetCollaboratorsResponse = z.infer<typeof AssetCollaboratorsResponse>;

export const GetAssetQuery = z
  .strictObject({
    versionId: AssetVersionId.optional().meta({
      description:
        "One of the asset's versions to read instead of its live one, such as the one a world pinned.",
    }),
  })
  .meta({ id: "GetAssetQuery", description: "Which version of the asset to read." });
export type GetAssetQuery = z.infer<typeof GetAssetQuery>;

export const AssetIdParams = z
  .strictObject({ id: AssetId })
  .meta({ id: "AssetIdParams", description: "A route parameter naming one asset." });
export type AssetIdParams = z.infer<typeof AssetIdParams>;

/** A published asset's address, unique within its kind. */
export const AssetSlugParams = z
  .strictObject({ kind: AssetKind, slug: AssetSlug })
  .meta({ id: "AssetSlugParams", description: "An asset's kind and permanent slug." });
export type AssetSlugParams = z.infer<typeof AssetSlugParams>;

// Browsing and searching

const AssetCursor = z.string().min(1).max(512).meta({
  description:
    "Opaque position from a previous page's `nextCursor`. Omit for the first page. Pass it back unchanged; it belongs to one query, filter set, and sort.",
  example: EXAMPLE.cursor,
});

const NextCursor = z.string().nullable().meta({
  description:
    "Send as `cursor` to fetch the next page, or `null` on the last page. It is the only reliable end-of-list signal.",
  example: EXAMPLE.cursor,
});

const QueryFlag = z.enum(["true", "false"]);

export const AssetSort = z.enum(["newest", "mostUsed", "topRated"]).meta({
  id: "AssetSort",
  description:
    "`newest` published first, `mostUsed` by how often games have used them, or `topRated` by likes minus dislikes.",
  example: "newest",
});
export type AssetSort = z.infer<typeof AssetSort>;

const assetFilters = {
  kind: AssetKind.optional().meta({ description: "Only assets of this kind." }),
  withAllTags: z
    .string()
    .regex(commaSeparated(TAG_PATTERN, ASSET_TAG_MAX_COUNT))
    .optional()
    .meta({
      description: `Only assets carrying every one of these tags. Comma-separated, up to ${ASSET_TAG_MAX_COUNT}.`,
      example: "flag,prussia",
    }),
  inAnyCollection: z
    .string()
    .regex(commaSeparated(UUID_PATTERN, ASSET_SEARCH_MAX_COLLECTIONS))
    .optional()
    .meta({
      description: `Only assets in at least one of these collections. Comma-separated ids, up to ${ASSET_SEARCH_MAX_COLLECTIONS}. Each must be public, or private and owned by the caller or shared with them as a collaborator.`,
      example: EXAMPLE.collectionId,
    }),
  language: AssetLanguage.optional().meta({
    description: "Only assets in exactly this language. `zxx` matches only assets with no words.",
  }),
  humanVerifiedOnly: QueryFlag.optional().meta({
    description: "`true` for only assets Pax staff verified.",
  }),
  excludeAiGenerated: QueryFlag.optional().meta({
    description: "`true` to leave out assets known to be AI-generated. Unknown ones stay in.",
  }),
};

const AssetQueryText = z.string().trim().toLowerCase().min(1).max(500).meta({
  description:
    "What to look for, in any language, such as `black eagle on a white flag`. Assets are matched by meaning, not only by words.",
  example: "flag of prussia",
});

const includeSensitive = QueryFlag.default("true").meta({
  description: "`false` to leave out sensitive assets. Defaults to `true`.",
});

const assetPageLimit = z.coerce.number().int().min(1).max(50).default(24).meta({
  description:
    "Maximum assets per page, 1–50, default 24. The last page may be shorter; rely on `nextCursor`.",
  example: 24,
});

export const ListAssetsQuery = z
  .strictObject({
    ids: z.string().regex(commaSeparated(UUID_PATTERN, 50)).optional().meta({
      description:
        "Only these published assets, each at its live version, comma-separated, up to 50, in that order. Every other filter is ignored, and there is no next page.",
    }),
    versionIds: z.string().regex(commaSeparated(UUID_PATTERN, 50)).optional().meta({
      description:
        "Only these published versions, comma-separated, up to 50, in that order, such as the pictures a world pins. A removed version comes back with no media. Every other filter is ignored, and there is no next page.",
    }),
    ownerId: ProfileId.optional().meta({
      description:
        "Only this person's assets, most recently edited first instead of in `sort`. When they are the caller, the assets they collaborate on and their drafts are included too.",
    }),
    status: AssetListStatus.optional().meta({
      description:
        "Only assets with a draft, each at its newest draft, or only published ones. Needs `ownerId` set to the caller.",
    }),
    ...assetFilters,
    includeSensitive,
    sort: AssetSort.default("newest"),
    limit: assetPageLimit,
    cursor: AssetCursor.optional(),
  })
  .meta({
    id: "ListAssetsQuery",
    description: "Which assets to page through. Different filters all apply at once.",
    example: { kind: "image", withAllTags: "flag", sort: "mostUsed" },
  });
export type ListAssetsQuery = z.infer<typeof ListAssetsQuery>;

export const SearchAssetsQuery = z
  .strictObject({
    q: AssetQueryText,
    ...assetFilters,
    includeSensitive,
    limit: assetPageLimit,
    cursor: AssetCursor.optional(),
  })
  .meta({
    id: "SearchAssetsQuery",
    description: `What to search published assets for. Different filters all apply at once, and paging stops after the ${ASSET_SEARCH_MAX_RESULTS} best matches.`,
    example: { q: "flag of prussia", kind: "image", withAllTags: "flag" },
  });
export type SearchAssetsQuery = z.infer<typeof SearchAssetsQuery>;

export const AssetPage = z
  .strictObject({ assets: z.array(Asset), nextCursor: NextCursor })
  .meta({ id: "AssetPage", description: "One page of assets." });
export type AssetPage = z.infer<typeof AssetPage>;

const SearchSimilarity = z.number().min(-1).max(1).meta({
  description:
    "How close the asset's title, description, and tags are to the search, as the cosine similarity of their embeddings: 1 is the same meaning, and lower is further. Results come highest first. Compare it within one search; how high a good match scores depends on the query.",
  example: 0.71,
});

export const AssetSearchResult = z
  .strictObject({ asset: Asset, similarity: SearchSimilarity })
  .meta({ id: "AssetSearchResult", description: "A published asset that matches a search." });
export type AssetSearchResult = z.infer<typeof AssetSearchResult>;

export const AssetSearchPage = z
  .strictObject({
    results: z.array(AssetSearchResult).meta({ description: "The best matches first." }),
    nextCursor: NextCursor,
  })
  .meta({ id: "AssetSearchPage", description: "One page of the best matches for a search." });
export type AssetSearchPage = z.infer<typeof AssetSearchPage>;

export const ListAssetTagsQuery = z
  .strictObject({
    kind: AssetKind.optional().meta({
      description: "Count only assets of this kind. Without it, counts cover every kind.",
    }),
    prefix: z.string().trim().toLowerCase().min(1).max(ASSET_TAG_MAX_CHARACTERS).optional().meta({
      description: "Only tags starting with this, for autocomplete.",
      example: "fl",
    }),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .meta({
    id: "ListAssetTagsQuery",
    description: "Which tags to count.",
    example: { kind: "image", prefix: "fl" },
  });
export type ListAssetTagsQuery = z.infer<typeof ListAssetTagsQuery>;

export const AssetTagCount = z
  .strictObject({
    tag: AssetTag,
    assetCount: z.int().positive().meta({
      description: "Published assets carrying the tag.",
      example: 1240,
    }),
  })
  .meta({ id: "AssetTagCount", description: "A tag and how many published assets carry it." });
export type AssetTagCount = z.infer<typeof AssetTagCount>;

export const ListAssetTagsResponse = z
  .strictObject({
    tags: z.array(AssetTagCount).meta({ description: "The most used tags first." }),
  })
  .meta({ id: "ListAssetTagsResponse", description: "Tags across the whole library." });
export type ListAssetTagsResponse = z.infer<typeof ListAssetTagsResponse>;

// Collections

export const AssetCollectionVisibility = z.enum(["public", "private"]).meta({
  id: "AssetCollectionVisibility",
  description:
    "`public` for anyone to find and search, or `private` for only its owner, its collaborators, and their games.",
  example: "public",
});
export type AssetCollectionVisibility = z.infer<typeof AssetCollectionVisibility>;

const CollectionTitle = z
  .string()
  .trim()
  .min(1)
  .max(ASSET_COLLECTION_TITLE_MAX_CHARACTERS)
  .meta({
    description: `The collection's name, 1 to ${ASSET_COLLECTION_TITLE_MAX_CHARACTERS} characters.`,
    example: "Flags of the German states",
  });

const CollectionDescription = z
  .string()
  .trim()
  .max(ASSET_COLLECTION_DESCRIPTION_MAX_CHARACTERS)
  .meta({
    description: `What belongs in the collection, up to ${ASSET_COLLECTION_DESCRIPTION_MAX_CHARACTERS} characters.`,
    example: "Every state flag of the German Empire, 1871–1918.",
  });

export const AssetCollectionRole = z.enum(["owner", "collaborator", "curator"]).meta({
  id: "AssetCollectionRole",
  description:
    "`owner`: edits the collection, its assets, and its collaborators. `collaborator`: adds and removes its assets. `curator`: staff holding `official_collections.manage`, who do everything an owner does for an official collection.",
  example: "owner",
});
export type AssetCollectionRole = z.infer<typeof AssetCollectionRole>;

export const AssetCollectionPicture = z
  .strictObject({
    image: ImageSet.meta({
      description:
        "The whole picture as AVIF in its own shape, smallest first: an image in every size, or a video's poster. An animated image stays animated.",
    }),
    still: ImageSource.nullable().meta({
      description: `A JPEG of an animated image's first frame, at most ${ASSET_STILL_MAX_SIDE} pixels on its longest side, for viewers who prefer reduced motion. Null when the picture doesn't move.`,
    }),
    thumbhash: Thumbhash,
    sensitive: z.boolean().meta({
      description:
        "Whether the asset is marked sensitive, which only a cover its owner chose can be. Show the thumbhash until the viewer asks to see it, as for the asset itself.",
      example: false,
    }),
  })
  .meta({
    id: "AssetCollectionPicture",
    description: "The picture a collection's card is drawn with, uncropped.",
  });
export type AssetCollectionPicture = z.infer<typeof AssetCollectionPicture>;

export const AssetCollection = z
  .strictObject({
    id: AssetCollectionId,
    title: CollectionTitle,
    description: CollectionDescription,
    visibility: AssetCollectionVisibility,
    official: z.boolean().meta({
      description: "Whether Pax staff curate it. An official collection has no owner.",
      example: false,
    }),
    owner: ProfileSummary.nullable().meta({
      description: "Who curates it, or null when official.",
    }),
    assetCount: z
      .int()
      .min(0)
      .max(ASSET_COLLECTION_MAX_ASSETS)
      .meta({
        description: `How many assets it holds, at most ${ASSET_COLLECTION_MAX_ASSETS}.`,
        example: 38,
      }),
    cover: AssetCollectionPicture.nullable().meta({
      description:
        "The picture its card is drawn with: the asset its owner chose, or else the first asset with a picture that was added to it, leaving out sensitive ones. Null when it holds no pictures.",
    }),
    coverAssetId: AssetId.nullable().meta({
      description:
        "The asset its owner chose as the cover, or null when the first one added is used. Taking that asset out of the collection clears it.",
      example: null,
    }),
    forkedFrom: z.strictObject({ id: AssetCollectionId, title: CollectionTitle }).nullable().meta({
      description: "The collection this one was copied from, while that one still exists.",
    }),
    viewerRole: AssetCollectionRole.nullable().meta({
      description:
        "What the caller can do with the collection, or null when they can only view it.",
      example: null,
    }),
    createdAt: Timestamp,
    updatedAt: Timestamp,
  })
  .meta({
    id: "AssetCollection",
    description: "A named group of published assets that people curate and games search.",
    example: {
      id: EXAMPLE.collectionId,
      title: "Flags of the German states",
      description: "Every state flag of the German Empire, 1871–1918.",
      visibility: "public",
      official: false,
      owner: EXAMPLE.author,
      assetCount: 38,
      cover: null,
      coverAssetId: null,
      forkedFrom: null,
      viewerRole: null,
      createdAt: EXAMPLE.timestamp,
      updatedAt: EXAMPLE.timestamp,
    },
  });
export type AssetCollection = z.infer<typeof AssetCollection>;

export const AssetCollectionResponse = z
  .strictObject({
    collection: AssetCollection,
    moderation: ItemModeration.optional().meta({
      description:
        "The collection's moderation state: a refusal of its text, its latest appeal, or a removal. Returned only to its owner, by Get a collection.",
    }),
  })
  .meta({ id: "AssetCollectionResponse", description: "One collection." });
export type AssetCollectionResponse = z.infer<typeof AssetCollectionResponse>;

export const AssetCollectionPage = z
  .strictObject({ collections: z.array(AssetCollection), nextCursor: NextCursor })
  .meta({ id: "AssetCollectionPage", description: "One page of collections." });
export type AssetCollectionPage = z.infer<typeof AssetCollectionPage>;

export const AssetCollectionIdParams = z
  .strictObject({ collectionId: AssetCollectionId })
  .meta({ id: "AssetCollectionIdParams", description: "A route parameter naming one collection." });
export type AssetCollectionIdParams = z.infer<typeof AssetCollectionIdParams>;

export const AssetCollectionAssetParams = z
  .strictObject({ collectionId: AssetCollectionId, assetId: AssetId })
  .meta({
    id: "AssetCollectionAssetParams",
    description: "Route parameters naming one collection and one asset.",
  });
export type AssetCollectionAssetParams = z.infer<typeof AssetCollectionAssetParams>;

export const AssetCollectionCollaborator = z
  .strictObject({
    profile: ProfileSummary,
    addedAt: Timestamp,
  })
  .meta({
    id: "AssetCollectionCollaborator",
    description: "A person who can add and remove a collection's assets.",
  });
export type AssetCollectionCollaborator = z.infer<typeof AssetCollectionCollaborator>;

export const AssetCollectionCollaboratorsResponse = z
  .strictObject({
    collaborators: z
      .array(AssetCollectionCollaborator)
      .max(ASSET_COLLECTION_MAX_COLLABORATORS)
      .meta({
        description: `Everyone the owner added, most recently added first. A collection has at most ${ASSET_COLLECTION_MAX_COLLABORATORS}.`,
      }),
  })
  .meta({
    id: "AssetCollectionCollaboratorsResponse",
    description: "A collection's collaborators.",
  });
export type AssetCollectionCollaboratorsResponse = z.infer<
  typeof AssetCollectionCollaboratorsResponse
>;

export const AssetCollectionCollaboratorParams = z
  .strictObject({ collectionId: AssetCollectionId, profileId: ProfileId })
  .meta({
    id: "AssetCollectionCollaboratorParams",
    description: "Route parameters naming one collection and one person.",
  });
export type AssetCollectionCollaboratorParams = z.infer<typeof AssetCollectionCollaboratorParams>;

const CollectionTitleQuery = z
  .string()
  .trim()
  .toLowerCase()
  .min(1)
  .max(ASSET_COLLECTION_TITLE_MAX_CHARACTERS)
  .meta({
    description:
      "Words to find in the title, such as `germ flag`. Every word has to match a word in the title: its start, or the whole word with a typo or two. Results come most relevant first, then the larger.",
    example: "german flags",
  });

const officialFilter = QueryFlag.optional().meta({
  description: "`true` for only official collections, `false` for only players' collections.",
});

const editorFilter = ProfileId.optional().meta({
  description:
    "Only the collections this person owns or collaborates on. Private ones are included when the caller can see them.",
});

export const ListAssetCollectionsQuery = z
  .strictObject({
    ids: z.string().regex(commaSeparated(UUID_PATTERN, 50)).optional().meta({
      description:
        "Only these collections, comma-separated, up to 50, such as the ones a skill mentions. Private ones are included for their owner and collaborators.",
      example: EXAMPLE.collectionId,
    }),
    official: officialFilter,
    editorId: editorFilter,
    assetId: AssetId.optional().meta({
      description: "Only the collections holding this published asset.",
    }),
    sort: z.enum(["largest", "recentlyChanged"]).default("largest").meta({
      description: "`largest` first, the default, or the most `recentlyChanged` first.",
      example: "largest",
    }),
    limit: z.coerce.number().int().min(1).max(50).default(24),
    cursor: AssetCursor.optional(),
  })
  .meta({
    id: "ListAssetCollectionsQuery",
    description: "Which collections to page through. Different filters all apply at once.",
    example: { official: "true" },
  });
export type ListAssetCollectionsQuery = z.infer<typeof ListAssetCollectionsQuery>;

export const SearchAssetCollectionsQuery = z
  .strictObject({
    q: CollectionTitleQuery,
    official: officialFilter,
    editorId: editorFilter,
    limit: z.coerce.number().int().min(1).max(50).default(24),
    cursor: AssetCursor.optional(),
  })
  .meta({
    id: "SearchAssetCollectionsQuery",
    description: `What to find in collection titles, and which collections to look in. Paging stops after the ${ASSET_COLLECTION_SEARCH_MAX_RESULTS} most relevant matches.`,
    example: { q: "german flags" },
  });
export type SearchAssetCollectionsQuery = z.infer<typeof SearchAssetCollectionsQuery>;

export const ListCollectionAssetsQuery = z
  .strictObject({
    limit: z.coerce.number().int().min(1).max(50).default(24),
    cursor: AssetCursor.optional(),
  })
  .meta({
    id: "ListCollectionAssetsQuery",
    description: "How to page through a collection's assets, most recently added first.",
  });
export type ListCollectionAssetsQuery = z.infer<typeof ListCollectionAssetsQuery>;

export const CreateAssetCollectionRequest = z
  .strictObject({
    title: CollectionTitle,
    description: CollectionDescription.optional(),
    visibility: AssetCollectionVisibility.default("public"),
    official: z.boolean().default(false).meta({
      description:
        "`true` to create an official collection, which needs `official_collections.manage`.",
    }),
  })
  .meta({
    id: "CreateAssetCollectionRequest",
    description: "The new, empty collection.",
    example: { title: "Flags of the German states", visibility: "public" },
  });
export type CreateAssetCollectionRequest = z.infer<typeof CreateAssetCollectionRequest>;

export const UpdateAssetCollectionRequest = z
  .strictObject({
    title: CollectionTitle.optional(),
    description: CollectionDescription.optional(),
    visibility: AssetCollectionVisibility.optional(),
    coverAssetId: AssetId.nullable().optional().meta({
      description:
        "An asset in the collection with a picture, sensitive or not, to draw its card with, or null to go back to the first one added.",
    }),
  })
  .refine(
    body => Object.values(body).some(value => value !== undefined),
    "Send at least one field.",
  )
  .meta({
    id: "UpdateAssetCollectionRequest",
    description: "The fields to change. Leave a field out to keep it.",
    example: { visibility: "private" },
  });
export type UpdateAssetCollectionRequest = z.infer<typeof UpdateAssetCollectionRequest>;

export const ForkAssetCollectionRequest = z
  .strictObject({
    title: CollectionTitle.optional().meta({
      description: "The copy's name. Defaults to the original's.",
    }),
    visibility: AssetCollectionVisibility.optional().meta({
      description: "Who can see the copy. Defaults to the original's visibility.",
    }),
  })
  .meta({
    id: "ForkAssetCollectionRequest",
    description: "How to name and share the copy.",
    example: { title: "My German flags" },
  });
export type ForkAssetCollectionRequest = z.infer<typeof ForkAssetCollectionRequest>;

export const ListSimilarAssetsQuery = z
  .strictObject({
    includeSensitive: QueryFlag.default("true").meta({
      description: "`false` to leave out sensitive assets. Defaults to `true`.",
    }),
  })
  .meta({ id: "ListSimilarAssetsQuery", description: "Which similar assets to return." });
export type ListSimilarAssetsQuery = z.infer<typeof ListSimilarAssetsQuery>;

export const SimilarAsset = z
  .strictObject({
    asset: Asset,
    similarity: z.number().min(-1).max(1).meta({
      description:
        "How alike the two are, where 1 is the same. Its scale depends on the list it's in.",
      example: 0.84,
    }),
  })
  .meta({ id: "SimilarAsset", description: "A published asset, and how alike it is." });
export type SimilarAsset = z.infer<typeof SimilarAsset>;

export const SimilarAssets = z
  .strictObject({
    byMeaning: z.array(SimilarAsset).meta({
      description: `Up to ${ASSET_SIMILAR_MAX_PER_LIST} assets whose title, description, and tags mean the closest thing, closest first. \`similarity\` is the cosine similarity of their embeddings, from -1 to 1.`,
    }),
    byMedia: z.array(SimilarAsset).meta({
      description: `Up to ${ASSET_SIMILAR_MAX_PER_LIST} assets whose files are most alike, closest first. \`similarity\` is the share of their hashes' bits that agree, from 0 to 1. An image always gets ${ASSET_SIMILAR_MAX_PER_LIST} if there are that many, the nearest pictures however far: past about 90% they share only a rough layout and brightness. A video or a recording gets only the copies of it that were found, which are usually none.`,
    }),
  })
  .meta({
    id: "SimilarAssets",
    description:
      "Published assets of the same kind that are like an asset, by meaning and by their files. One asset can be in both lists.",
  });
export type SimilarAssets = z.infer<typeof SimilarAssets>;

export const AssetPresets = z
  .strictObject({
    presets: z.array(PresetListing).meta({
      description: `Up to ${ASSET_PRESETS_MAX} published scenarios, the one with the most games that used the asset first, then the one that used it last.`,
    }),
  })
  .meta({
    id: "AssetPresets",
    description: "The published scenarios whose games have used an asset.",
  });
export type AssetPresets = z.infer<typeof AssetPresets>;

export const AssetCopyMatch = z.enum(["file", "picture", "frames", "recording", "meaning"]).meta({
  id: "AssetCopyMatch",
  description:
    "What made it a copy: `file` the very same uploaded file, `picture` an image that looks the same, `frames` a video or an animation with the same frames, `recording` audio that sounds the same, or `meaning` a title, description, and tags that say the same.",
  example: "picture",
});
export type AssetCopyMatch = z.infer<typeof AssetCopyMatch>;

export const AssetCopy = z
  .strictObject({
    asset: Asset,
    matchedOn: AssetCopyMatch.meta({
      description: "Its closest match, when it matched in more than one way.",
    }),
    similarity: z.number().min(0).max(1).meta({
      description: "How alike the two are, from 0.95 to 1, where the very same file is 1.",
      example: 0.97,
    }),
  })
  .meta({ id: "AssetCopy", description: "A published asset that's a copy of the draft." });
export type AssetCopy = z.infer<typeof AssetCopy>;

export const AssetCopies = z
  .strictObject({
    unique: z.boolean().nullable().meta({
      description:
        "What publishing the draft now would record as its `wasUniqueAtPublish`: `false` when it has copies, `true` when it has none, and null when it has none but its file has nothing to compare, or it has no file yet.",
      example: false,
    }),
    copies: z.array(AssetCopy).meta({
      description: `Up to ${ASSET_COPIES_MAX} published copies, closest first.`,
    }),
  })
  .meta({
    id: "AssetCopies",
    description:
      "The published copies of a draft, found by the check publishing runs, and what publishing it now would record.",
  });
export type AssetCopies = z.infer<typeof AssetCopies>;

export const AssetUsageRequest = z
  .strictObject({
    usageId: z.uuid().meta({
      description:
        "An id the game master picks for this use, a UUIDv7. A retry sends the same id, so the use counts once.",
      example: EXAMPLE.usageId,
    }),
    query: AssetQueryText.optional().meta({
      description:
        "The search text the game master found the asset with. Leave it out when it didn't search, such as when it reused an asset it already had.",
      example: "flag of prussia",
    }),
  })
  .meta({
    id: "AssetUsageRequest",
    description: "One use of a published asset in a game, which adds one to its `useCount`.",
    example: { usageId: EXAMPLE.usageId, query: "flag of prussia" },
  });
export type AssetUsageRequest = z.infer<typeof AssetUsageRequest>;
